class PredictiveSearch extends window.SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.input.addEventListener("click", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.renderEmptyState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.target !== this.input || event.isComposing) return;
    if (!this.getQuery().length) this.renderEmptyState();

    switch (event.code) {
      case "ArrowUp":
        event.preventDefault();
        this.switchOption("up");
        break;
      case "ArrowDown":
        event.preventDefault();
        this.switchOption("down");
        break;
    }
  }

  onKeydown(event) {
    if (
      event.target !== this.input ||
      event.isComposing ||
      event.keyCode === 229
    ) {
      return;
    }

    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
      return;
    }

    if (event.key !== "Enter" && event.code !== "Enter") return;

    event.preventDefault();

    if (event.repeat) return;
    if (this.selectOption() || !this.getQuery().length) return;

    const form = this.input.form;
    if (typeof form?.requestSubmit === "function") {
      form.requestSubmit();
    } else if (typeof form?.submit === "function") {
      form.submit();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const matches = currentButtonText.match(new RegExp(previousTerm, "g"));
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (!selectedOption) return false;

    selectedOption.click();
    return true;
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.abortController.abort();
    this.abortController = new AbortController();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const shopifyResultsPromise = this.fetchShopifyPredictiveResults(searchTerm);
    const aiResultsPromise = this.fetchAISearchResults(searchTerm);

    shopifyResultsPromise
      .then((shopifyResultsMarkup) => {
        if (!this.isCurrentSearchTerm(searchTerm) || !shopifyResultsMarkup) return;

        this.renderSearchResults(shopifyResultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === "AbortError") {
          // Code 20 means the call was aborted
          return;
        }
        console.error("Search error:", error);
        this.close();
      });

    Promise.all([shopifyResultsPromise, aiResultsPromise])
      .then(([shopifyResultsMarkup, aiResults]) => {
        if (!this.isCurrentSearchTerm(searchTerm)) return;

        if (!aiResults?.results?.length) {
          if (shopifyResultsMarkup) this.cacheResults(queryKey, shopifyResultsMarkup);
          if (!shopifyResultsMarkup) this.close();
          return;
        }

        const combinedMarkup = this.mergeSearchResults(
          shopifyResultsMarkup,
          aiResults,
          searchTerm
        );

        if (!combinedMarkup) return;

        this.cacheResults(queryKey, combinedMarkup);
        this.renderSearchResults(combinedMarkup);
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === "AbortError") return;
        console.warn("AI search enrichment failed:", error);
      });
  }

  cacheResults(queryKey, resultsMarkup) {
    // Save bandwidth keeping the cache in all instances synced
    this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
      predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
    });
  }

  isCurrentSearchTerm(searchTerm) {
    return this.searchTerm === searchTerm && this.getQuery() === searchTerm;
  }

  /**
   * Fetch Shopify's native predictive search results
   */
  fetchShopifyPredictiveResults(searchTerm) {
    return fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        return new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === "AbortError") {
          throw error;
        }
        console.warn("Shopify predictive search failed:", error);
        return ""; // Return empty on error
      });
  }

  /**
   * Fetch AI-powered search results from the API
   */
  fetchAISearchResults(searchTerm) {
    if (!window.productAISearch) {
      return Promise.resolve({ results: [] });
    }

    return window.productAISearch
      .searchProducts(searchTerm, {
        limit: 5,
        signal: this.abortController.signal,
        timeout: window.getAISearchConfig?.("search.predictiveTimeout", 900) || 900,
        retries: 0,
      })
      .catch((error) => {
        if (error?.name === "AbortError") {
          throw error;
        }
        console.warn("AI search failed:", error);
        return { results: [] };
      });
  }

  /**
   * Merge Shopify results with AI search results
   */
  mergeSearchResults(shopifyMarkup, aiResults, searchTerm) {
    const wrapper = document.createElement("div");

    // Parse Shopify results
    if (shopifyMarkup) {
      wrapper.innerHTML = shopifyMarkup;
    }

    const safeAIResults = (aiResults?.results || []).filter((result) =>
      this.getSafeResultUrl(result)
    );

    // Add verified storefront results directly into the product list if available
    if (safeAIResults.length > 0) {
      // Find the product results list using the correct ID
      let productsList = wrapper.querySelector(
        "#predictive-search-results-products-list"
      );

      // If the list doesn't exist (no Shopify results), create it
      if (!productsList) {
        const resultGroup = document.createElement("div");
        resultGroup.className = "predictive-search__result-group";

        const heading = document.createElement("h2");
        heading.id = "predictive-search-products";
        heading.className =
          "predictive-search__heading text-body caption-with-letter-spacing";
        heading.textContent = "Produits";

        productsList = document.createElement("ul");
        productsList.id = "predictive-search-results-products-list";
        productsList.className =
          "predictive-search__results-list list-unstyled";
        productsList.setAttribute("role", "group");
        productsList.setAttribute(
          "aria-labelledby",
          "predictive-search-products"
        );

        // Create wrapper div (matching Shopify structure)
        const innerDiv = document.createElement("div");
        innerDiv.appendChild(heading);
        innerDiv.appendChild(productsList);

        resultGroup.appendChild(innerDiv);
        wrapper.appendChild(resultGroup);
      }

      // Add AI results to the product list
      safeAIResults.forEach((result) => {
        const item = this.createAIProductItem(result);
        if (item) productsList.appendChild(item);
      });
    }

    return wrapper.innerHTML;
  }

  /**
   * Create a single AI product list item
   */
  createAIProductItem(product) {
    const title = product.title || product.name || "Résultat";
    const url = this.getSafeResultUrl(product);
    if (!url) return null;

    const image = this.getSafeImageUrl(product.image || product.images?.[0]);
    const firstVariant = product.variants?.[0];
    const priceValue = product.price || firstVariant?.price;
    const typeLabel = this.getSearchResultTypeLabel(product.type);
    const item = document.createElement("li");
    item.className = "predictive-search__list-item";
    item.setAttribute("role", "option");
    item.setAttribute("aria-selected", "false");

    const link = document.createElement("a");
    link.href = url;
    link.className =
      "predictive-search__item predictive-search__item--link-with-thumbnail link link--text";
    link.setAttribute("tabindex", "-1");

    // Create product content
    const price = priceValue ? `${Number(priceValue).toFixed(2)}€` : null;

    link.innerHTML = `
      ${
        image
          ? `<img class="predictive-search__image" src="${this.escapeHtml(
              image
            )}" alt="${this.escapeHtml(title)}" width="50" height="50" loading="lazy">`
          : '<div class="predictive-search__image" style="background-color: #f0f0f0; width: 50px; height: 50px;"></div>'
      }
      <div class="predictive-search__item-content">
        <span class="visually-hidden">Fournisseur&nbsp;:</span>
        ${
          product.vendor
            ? `<div class="predictive-search__item-vendor caption-with-letter-spacing">${this.escapeHtml(
                product.vendor
              )}</div>`
            : product.excerpt
              ? `<div class="predictive-search__item-vendor caption-with-letter-spacing">${this.escapeHtml(
                  product.excerpt
                )}</div>`
            : ""
        }
        <p class="predictive-search__item-heading h5">
          ${this.escapeHtml(title)}
        </p>
        ${
          price
            ? `<div class="price"><div class="price__container"><div class="price__regular"><span class="visually-hidden visually-hidden--inline">Prix habituel</span><span class="price-item price-item--regular">${price}</span></div></div></div>`
            : `<span class="predictive-search__ai-type">${this.escapeHtml(typeLabel)}</span>`
        }
      </div>
    `;

    item.appendChild(link);
    return item;
  }

  getSafeResultUrl(result) {
    return window.productAISearch?.getSafeResultUrl?.(result) || null;
  }

  getSafeImageUrl(imageUrl) {
    if (!imageUrl || typeof imageUrl !== "string") return null;

    try {
      const parsedUrl = new URL(imageUrl, window.location.origin);
      return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:"
        ? parsedUrl.href
        : null;
    } catch {
      return null;
    }
  }

  getSearchResultTypeLabel(type) {
    if (type === "product") return "Produit";
    if (type === "collection") return "Collection";
    if (type === "article") return "Article";
    return "Page";
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const map = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  renderEmptyState() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.predictiveSearchResults.innerHTML = this.getEmptyStateMarkup();
    this.setAttribute("results", true);
    this.removeAttribute("loading");
    this.open();
  }

  getEmptyStateMarkup() {
    const popularCollections = [
      { title: "Télescopes", url: "/collections/telescopes" },
      { title: "Oculaires", url: "/collections/oculaires" },
      { title: "Montures", url: "/collections/montures" },
      { title: "Caméras", url: "/collections/cameras" },
      { title: "Filtres", url: "/collections/filtres-n" },
    ];
    const popularSearches = [
      "Télescope débutant",
      "Oculaire 1.25",
      "Filtre anti-pollution",
      "Caméra planétaire",
      "Monture équatoriale",
    ];

    const collectionItems = popularCollections
      .map(
        (collection, index) => `
          <li id="predictive-search-empty-collection-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${collection.url}" class="predictive-search__item predictive-search__item--empty-state link link--text" tabindex="-1">
              <span class="predictive-search__empty-icon" aria-hidden="true">${this.escapeHtml(collection.title.charAt(0))}</span>
              <span class="predictive-search__item-content predictive-search__item-content--centered">
                <span class="predictive-search__item-heading h5">${this.escapeHtml(collection.title)}</span>
              </span>
            </a>
          </li>`
      )
      .join("");

    const searchItems = popularSearches
      .map((term, index) => {
        const searchUrl = routes?.search_url || "/search";
        const url = `${searchUrl}?q=${encodeURIComponent(term)}`;
        return `
          <li id="predictive-search-empty-query-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
            <a href="${url}" class="predictive-search__item link link--text" tabindex="-1">
              <span class="predictive-search__item-content predictive-search__item-content--centered">
                <span class="predictive-search__item-heading h5">${this.escapeHtml(term)}</span>
              </span>
            </a>
          </li>`;
      })
      .join("");

    return `
      <div id="predictive-search-results" class="predictive-search__empty-state" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--empty-state">
          <div class="predictive-search__result-group">
            <h2 id="predictive-search-empty-collections" class="predictive-search__heading text-body caption-with-letter-spacing">Catégories populaires</h2>
            <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-empty-collections">
              ${collectionItems}
            </ul>
          </div>
          <div class="predictive-search__result-group">
            <h2 id="predictive-search-empty-searches" class="predictive-search__heading text-body caption-with-letter-spacing">Recherches fréquentes</h2>
            <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-empty-searches">
              ${searchItems}
            </ul>
          </div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value>Suggestions de recherche populaires</span>
      </div>`;
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const resultsCount = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    this.setLiveRegionText(resultsCount?.textContent || "");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
